/**
 * Convert a MediaRecorder blob (webm/opus or mp4/aac) to a mono 16-bit PCM WAV.
 *
 * MediaRecorder live-stream blobs carry no duration header, so <audio> players
 * often play only the first ~1s and seeking is broken; Safari/iOS can't decode
 * webm/opus at all. Re-encoding to WAV (which has an explicit data length)
 * makes voice notes play in full everywhere - the panel and the Manager app.
 */
export async function blobToWav(blob: Blob): Promise<Blob> {
  const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
  const ctx = new AC();
  try {
    const audioBuffer: AudioBuffer = await ctx.decodeAudioData(await blob.arrayBuffer());
    const length = audioBuffer.length;
    const numCh = audioBuffer.numberOfChannels || 1;
    const rate = audioBuffer.sampleRate;
    const mono = new Float32Array(length);
    for (let ch = 0; ch < numCh; ch++) {
      const data = audioBuffer.getChannelData(ch);
      for (let i = 0; i < length; i++) mono[i] += data[i] / numCh;
    }
    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);
    const writeStr = (off: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + length * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, length * 2, true);
    let off = 44;
    for (let i = 0; i < length; i++) { const s = Math.max(-1, Math.min(1, mono[i])); view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true); off += 2; }
    return new Blob([buffer], { type: 'audio/wav' });
  } finally {
    try { ctx.close?.(); } catch { /* ignore */ }
  }
}
